import { SerializedStyles, css } from '@emotion/react';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import { ChangeEvent, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { isDefined } from 'shared';

import { InputField } from '../forms/ui';
import { theme } from '../theme';

const styles = {
  inputFields: css`
    display: flex;
    gap: ${theme.spacing.sm};

    > div {
      flex: 1;
    }
  `,
  compactDateStyles: {
    bold: css`
      font-weight: ${theme.fontWeight.bold};
    `,
    strike: css`
      text-decoration: line-through;
    `,
  },
};

type FlightDatesCompactProps = {
  loading: boolean;
  outboundDate: string | undefined;
  inboundDate: string | undefined;
  styling?: SerializedStyles;
};

export function FlightDatesCompact({ loading, outboundDate, inboundDate, styling }: FlightDatesCompactProps) {
  return (
    <div css={styling}>
      {loading ? <Skeleton style={{ width: 60, display: 'inline-block' }} /> : getDates(outboundDate, inboundDate)}
    </div>
  );
}

function getDates(from?: string, to?: string) {
  return [from, to].filter(isDefined).map(formatDate).join(' - ');
}

const formatDate = (date: string) => date && DateTime.fromISO(date).toFormat('dd MMM');

type FlightDatesProps = {
  outboundDate: string | undefined;
  inboundDate: string | undefined;
  isRoundTrip: boolean;
};

export function FlightDates({ outboundDate, inboundDate, isRoundTrip }: FlightDatesProps) {
  const [outDate, setOutbound] = useState(outboundDate);
  const [inDate, setInbound] = useState(inboundDate);

  useEffect(() => {
    setOutbound(outboundDate);
  }, [outboundDate]);

  useEffect(() => {
    setInbound(inboundDate);
  }, [inboundDate]);

  const formatDateTime = (date: string | undefined) => date && DateTime.fromISO(date).toFormat('yyyy-MM-dd');
  return (
    <div css={styles.inputFields}>
      <InputField
        label={t('common:outboundDate')}
        value={formatDateTime(outDate)}
        iconName={'cl--calendar'}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setOutbound(e.target.value)}
        required
      />
      {isRoundTrip && (
        <InputField
          label={t('common:returnDate')}
          value={formatDateTime(inDate)}
          iconName={'cl--calendar'}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setInbound(e.target.value)}
          required
        />
      )}
    </div>
  );
}
